import {
  ApiEffectivenessConditionGroup,
  ApiOperator,
  ApiProject,
  ApiEffectivenessRule,
  ApiConditionCustom,
  ApiSessionConditionCustom,
  EffectivenessRuleServiceUpdateRuleRequest,
  SessionEffectivenessRuleServiceUpdateRuleRequest,
  EffectivenessRuleServiceCreateRuleRequest,
  SessionEffectivenessRuleServiceCreateRuleRequest,
  ApiSessionEffectivenessRule,
} from "@incendium/api";
import {
  effectivenessRuleService,
  sessionEffectivenessRuleService,
} from "Apis";
import { useConditionGroupContext } from "Components/ConditionalGroup/ConditionGroupProvider";
import ConditionGroupsFormWrapper from "Components/ConditionalGroup/ConditionGroupsFormWrapper";
import { enumToArray } from "Helpers/enumToText";
import { isEmpty } from "Helpers/isEmpty";
import { useEffectivenessRules } from "Hooks/useEffectivenessRules";
import { useSessionEffectivenessRules } from "Hooks/useSessionEffectivenessRules";
import produce from "immer";
import { TEffectivenessRule } from "Interfaces";
import { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import EffectivenessRules from "./EffectivenessRules";
import EffectivenessSidebar from "features/effectiveness/components/EffectivenessSidebar";
import { useNotification } from "Hooks";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { RouteNames } from "Hooks/routeNames";
interface IEffectivenessFormProps {
  rule: TEffectivenessRule;
  setRule: React.Dispatch<React.SetStateAction<TEffectivenessRule | undefined>>;
  project: ApiProject;
  isPageview: boolean;
}

function EffectivenessForm({
  rule,
  setRule,
  project,
  isPageview,
}: IEffectivenessFormProps) {
  const { conditionGroups, setConditionGroups } = useConditionGroupContext();
  const { effectivenessRules, setEffectivenessRules } = useEffectivenessRules();
  const { sessionEffectivenessRules, setSessionEffectivenessRules } =
    useSessionEffectivenessRules();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const history = useHistory();
  const { pathByName } = useProtectedRoutes();
  const types = useMemo(() => {
    if (isPageview) {
      return [
        ApiConditionCustom.CONVERSION_TRIGGERED,
        ApiConditionCustom.CONVERSION_COUNT,
        ApiConditionCustom.CONVERSION_MACRO_COUNT,
        ApiConditionCustom.CONVERSION_MICRO_COUNT,
        ApiConditionCustom.TIME_ON_PAGE_OVER,
        ApiConditionCustom.SCROLL_RATE_OVER,
        ApiConditionCustom.VIEW_RATE_OVER,
        ApiConditionCustom.TIME_ON_PAGE_PERCENTILE,
        ApiConditionCustom.SCROLL_RATE_PERCENTILE,
        ApiConditionCustom.VIEW_RATE_PERCENTILE,
        ApiConditionCustom.PAGE_SCORE,
      ];
    }
    return enumToArray(ApiSessionConditionCustom).filter(
      (e) => e !== ApiSessionConditionCustom.UNSPECIFIED,
    );
  }, [isPageview]);

  useEffect(() => {
    return () => {
      setConditionGroups([]);
      setRule(undefined);
    };
  }, []);

  useEffect(() => {
    if (!rule || conditionGroups.length) {
      return;
    }

    const groups = rule.conditionGroups || [];
    setConditionGroups(
      groups && groups.length > 0
        ? [...groups]
        : [
            {
              conditions: [{}],
            },
          ],
    );
  }, [rule, setConditionGroups, conditionGroups]);

  const onSubmit = async (noReload?: boolean) => {
    const req:
      | EffectivenessRuleServiceUpdateRuleRequest
      | SessionEffectivenessRuleServiceUpdateRuleRequest = {
      projectId: project.id as number,
      ruleId: rule.id as number,
      payload: {
        name: rule.name,
        conditionGroups: (conditionGroups as ApiEffectivenessConditionGroup[])
          .filter((c) => {
            return (
              !isEmpty(c) &&
              (c.conditions || []).filter((co) => !isEmpty(co)).length > 0
            );
          })
          .map((c) => ({
            conditions: (c.conditions || []).map((condition) => {
              if (
                condition.custom === ApiConditionCustom.CONVERSION_TRIGGERED
              ) {
                return {
                  ...condition,
                  operator: ApiOperator.EQUAL,
                };
              }
              return {
                ...condition,
                operator:
                  condition.operator &&
                  condition.operator !== ApiOperator.OPERATOR_UNASSIGNED
                    ? condition.operator
                    : ApiOperator.IS_GREATER_THAN_OR_EQUAL,
              };
            }),
          })),
      },
    };

    if (isPageview) {
      req.payload.locations = (
        (rule as ApiEffectivenessRule).locations || []
      ).map((l) => l.id as number);
    }

    try {
      let res: ApiEffectivenessRule | ApiSessionEffectivenessRule;
      if (isPageview) {
        res = rule.id
          ? await effectivenessRuleService.effectivenessRuleServiceUpdateRule(
              req as EffectivenessRuleServiceUpdateRuleRequest,
            )
          : await effectivenessRuleService.effectivenessRuleServiceCreateRule(
              req as EffectivenessRuleServiceCreateRuleRequest,
            );
        // update list
        setEffectivenessRules(
          produce(effectivenessRules, (draft) => {
            const idx = draft.findIndex((e) => e.id === res.id);
            if (idx < 0) {
              draft.push(res as ApiEffectivenessRule);
            } else {
              draft[idx] = res as ApiEffectivenessRule;
            }
          }),
        );
      } else {
        res = rule.id
          ? await sessionEffectivenessRuleService.sessionEffectivenessRuleServiceUpdateRule(
              req as SessionEffectivenessRuleServiceUpdateRuleRequest,
            )
          : await sessionEffectivenessRuleService.sessionEffectivenessRuleServiceCreateRule(
              req as SessionEffectivenessRuleServiceCreateRuleRequest,
            );
        setSessionEffectivenessRules(
          produce(sessionEffectivenessRules, (draft) => {
            const idx = draft.findIndex((e) => e.id === res.id);
            if (idx < 0) {
              draft.push(res as ApiSessionEffectivenessRule);
            } else {
              draft[idx] = res as ApiSessionEffectivenessRule;
            }
          }),
        );
      }

      if (!rule.id && !noReload) {
        // navigate to the new rule page
        history.push(
          pathByName(
            isPageview
              ? RouteNames.EFFECTIVENESS_PAGEVIEWS
              : RouteNames.EFFECTIVENESS_SESSIONS,
            {
              clientId: project.clientId,
              projectId: project.id,
              ruleId: res.id as number,
            },
          ),
        );
      }

      showSuccessNotification(`${res.name} Saved`);
    } catch (error) {
      const resp = await (error as any).json();
      showErrorNotification(resp.message);
    }
  };

  const onCancel = () => {
    history.push(
      pathByName(RouteNames.EFFECTIVENESS, {
        clientId: project.clientId,
        projectId: project.id,
      }),
    );
  };

  const onSaveAndExit = async () => {
    await onSubmit(true);
    history.push(
      pathByName(RouteNames.EFFECTIVENESS, {
        clientId: project.clientId,
        projectId: project.id,
      }),
    );
  };
  return (
    <ConditionGroupsFormWrapper
      onSubmit={onSubmit}
      onCancel={onCancel}
      onSaveAndExit={onSaveAndExit}
      rules={<EffectivenessRules types={types} />}
    >
      <EffectivenessSidebar
        effectiveness={rule}
        setEffectiveness={setRule}
        isPageview={isPageview}
      />
    </ConditionGroupsFormWrapper>
  );
}

export default EffectivenessForm;
